/**
 * Formatação de datas "puras" (sem hora significativa).
 *
 * O backend entrega esses valores como `YYYY-MM-DD` (colunas `date` do Postgres)
 * ou como `YYYY-MM-DD 00:00:00+00` (colunas `timestamptz` do DW, onde a meia-noite
 * UTC é só o preenchimento). `new Date(str).toLocaleDateString()` interpreta os dois
 * como UTC e desloca -1 dia em fuso negativo (Manaus é UTC-4), fazendo uma venda de
 * 01/07 aparecer como 30/06 — e cair no trimestre errado aos olhos do usuário.
 *
 * Para timestamps em que a hora importa (created_at, pago_em) use `new Date(...)`
 * normal: lá a conversão de fuso é justamente o comportamento desejado.
 */
export function formatDateOnlyBR(value: string | null | undefined): string {
    if (!value) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return new Date(value).toLocaleDateString('pt-BR');
}

const ISO_DATETIME_IN_TEXT = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})/g;

/** `2026-10-01T11:55:00+00:00` → `01/10/2026 às 08:55` (no fuso do navegador). */
export function formatDateTimeBR(value: string, timeZone?: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const parts = Object.fromEntries(
        new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone })
            .formatToParts(d)
            .map((p) => [p.type, p.value]),
    );
    return `${parts.day}/${parts.month}/${parts.year} às ${parts.hour}:${parts.minute}`;
}

/**
 * Troca timestamps ISO 8601 embutidos em texto livre pelo formato pt-BR.
 * Existe porque notificações antigas foram gravadas com a data crua na mensagem
 * (`Prazo: 2026-10-01T11:55:00+00:00`) — formatar na exibição cobre as já salvas.
 */
export function formatIsoDatesInTextBR(text: string, timeZone?: string): string {
    return text.replace(ISO_DATETIME_IN_TEXT, (iso) => formatDateTimeBR(iso, timeZone));
}
